import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  formatHoldMinutes,
  type GuestBooking,
  type GuestCancelled,
  type RefundTier,
} from '@kratispot/shared';
import { PolicyNote } from '@/components/visitor/book-modal';
import { LanguageToggle } from '@/components/language-toggle';
import { Toasts, useToasts } from '@/components/toasts';
import { Button } from '@/components/ui/button';
import { ApiError, cancelBooking, extendBooking, lookupBooking } from '@/lib/api';
import { forgetOwnBooking, rememberOwnBooking } from '@/lib/checkout-memory';
import { leaveFor } from '@/lib/leave-for';
import { pendingCheckout } from '@/lib/pending-checkout';
import { moneyOf } from '@/lib/format';
import { useTranslation, type CopyKey } from '@/lib/i18n';

const INPUT =
  'h-11 w-full rounded-xl border bg-background px-3 text-base outline-none focus:ring-2 focus:ring-ring';

export const TIER_COPY: Readonly<Record<RefundTier, CopyKey>> = {
  full: 'tierFull',
  partial: 'tierPartial',
  none: 'tierNone',
};

function Row({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <span className="flex items-baseline justify-between gap-3 py-1 text-[13px]">
      <span className="text-muted-foreground">{label}</span>
      <b className="text-end">{value}</b>
    </span>
  );
}

// §12: one lookup by booking code and email, serving two actions. Not in the
// mockup as a screen — its "My booking" modal is the idiom this follows.

// Rate-limited is not the same answer as wrong; an unpaid one has its own card.
export function lookupFailure(error: unknown): CopyKey {
  const limited = error instanceof ApiError && error.status === 429;
  return limited ? 'bookingTooMany' : 'bookingNotFound';
}

export function BookingPage(): ReactElement {
  const { t, language } = useTranslation();
  const slug = useParams().slug as string;
  const [query] = useSearchParams();
  const [code, setCode] = useState(query.get('code') ?? '');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState<GuestBooking | null>(null);
  const [asking, setAsking] = useState(false);
  const [cancelled, setCancelled] = useState<GuestCancelled | null>(null);
  // A checkout still in flight is not a booking yet, and not a missing one. §12
  const [waiting, setWaiting] = useState<{ payUrl: string | null } | null>(null);
  const [minutes, setMinutes] = useState<number | null>(null);
  const { toasts, push } = useToasts();

  async function find(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    try {
      setFound(await lookupBooking(slug, { code, email }));
      // The tab now holds this booking, so the venue's own floor marks it. §12
      rememberOwnBooking({ slug, code, email });
      setCancelled(null);
      setWaiting(null);
      setAsking(false);
      setMinutes(null);
    } catch (error) {
      setFound(null);
      const unpaid = pendingCheckout(error);
      setWaiting(unpaid);
      if (unpaid === null) push(t(lookupFailure(error)), 'bg-red-500');
    } finally {
      setBusy(false);
    }
  }

  async function extend(holdMinutes: number): Promise<void> {
    setBusy(true);
    try {
      const started = await extendBooking(slug, { code, email, holdMinutes });
      if (started.redirectUrl !== null) {
        leaveFor(started.redirectUrl);
        return;
      }
      setFound(await lookupBooking(slug, { code, email }));
      push(t('extended'), 'bg-emerald-600');
    } catch {
      push(t('actionFailed'), 'bg-red-500');
    } finally {
      setBusy(false);
    }
  }

  async function stop(): Promise<void> {
    setBusy(true);
    try {
      setCancelled(await cancelBooking(slug, { code, email }));
      forgetOwnBooking();
      setFound(null);
      setAsking(false);
    } catch {
      push(t('actionFailed'), 'bg-red-500');
    } finally {
      setBusy(false);
    }
  }

  const choice = found?.extensions.find((each) => each.minutes === minutes) ?? found?.extensions[0];

  return (
    <div className="flex min-h-full flex-col bg-background px-safe pt-safe pb-safe">
      <header className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2.5">
        <Link to={`/${slug}`} className="truncate text-[15px] font-extrabold text-primary">
          {t('back')}
        </Link>
        <LanguageToggle />
      </header>

      <div className="mx-auto flex w-full max-w-[440px] flex-col gap-3 p-4">
        <h2 className="text-[17px] font-extrabold">{t('manageBooking')}</h2>
        <form onSubmit={(event) => void find(event)} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-[13px]">
            <span className="font-medium text-muted-foreground">{t('bookingCode')}</span>
            <input
              className={INPUT}
              value={code}
              required
              onChange={(event) => setCode(event.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-[13px]">
            <span className="font-medium text-muted-foreground">{t('email')}</span>
            <input
              type="email"
              className={INPUT}
              value={email}
              required
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <Button type="submit" disabled={busy}>
            {t('findBooking')}
          </Button>
        </form>

        {found !== null && (
          <section
            aria-label={t('bookingTitle')}
            className="flex flex-col rounded-2xl border bg-card p-4"
          >
            <b className="text-[15px] font-extrabold">{found.code}</b>
            <Row
              label={t('spotsLabel')}
              value={found.spots.map((spot) => `#${spot.number} · ${spot.zoneName}`).join(', ')}
            />
            <Row
              label={t('dates')}
              value={
                found.startsOn === found.endsOn
                  ? found.startsOn
                  : `${found.startsOn} – ${found.endsOn}`
              }
            />
            <Row label={t('heldUntil')} value={formatHoldMinutes(found.holdMinutes)} />
            <Row
              label={t('paidLabel')}
              value={moneyOf(found.paidCents, found.currency, language)}
            />

            {found.seated ? (
              <p className="mt-2 text-[12.5px] text-muted-foreground">{t('guestSeated')}</p>
            ) : (
              choice !== undefined && (
                <div className="mt-3 flex flex-col gap-2">
                  <label className="flex flex-col gap-1 text-[13px]">
                    <span className="font-medium text-muted-foreground">{t('newTime')}</span>
                    <select
                      className={INPUT}
                      value={choice.minutes}
                      onChange={(event) => setMinutes(Number(event.target.value))}
                    >
                      {found.extensions.map((each) => (
                        <option key={each.minutes} value={each.minutes}>
                          {formatHoldMinutes(each.minutes)} · +
                          {moneyOf(each.feeCents, found.currency, language)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <p className="text-[11.5px] text-muted-foreground">{t('extendOnlineNote')}</p>
                  <Button disabled={busy} onClick={() => void extend(choice.minutes)}>
                    {t('payExtend')}
                  </Button>
                </div>
              )
            )}

            <div className="mt-3 border-t pt-3">
              <PolicyNote policy={found.cancellation} />
              {asking ? (
                <div className="mt-2 flex flex-col gap-2 rounded-xl border border-red-200 bg-red-50/60 p-3">
                  <Row label={t('policyLine')} value={t(TIER_COPY[found.refund.tier])} />
                  <Row
                    label={t('refundFeeLine')}
                    value={moneyOf(found.refund.feeCents, found.currency, language)}
                  />
                  <Row
                    label={t('refundLine')}
                    value={moneyOf(found.refund.refundCents, found.currency, language)}
                  />
                  {/* A Button never shrinks or wraps its label, so the row must. §20 */}
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" className="grow" onClick={() => setAsking(false)}>
                      {t('keepBooking')}
                    </Button>
                    <Button
                      variant="outline"
                      className="grow border-red-300 text-red-700"
                      disabled={busy}
                      onClick={() => void stop()}
                    >
                      {t('confirmCancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="mt-2 w-full border-red-300 text-red-700"
                  onClick={() => setAsking(true)}
                >
                  {t('cancelBooking')}
                </Button>
              )}
            </div>
          </section>
        )}

        {waiting !== null && (
          <section
            aria-label={t('bookingPending')}
            className="flex flex-col gap-2 rounded-2xl border border-amber-300 bg-amber-50/70 p-4"
          >
            <b className="text-[15px] font-extrabold text-amber-900">{t('bookingPending')}</b>
            <p className="text-[13px] text-amber-900">{t('bookingPendingNote')}</p>
            {waiting.payUrl !== null && (
              <Button onClick={() => leaveFor(waiting.payUrl as string)}>
                {t('resumePayment')}
              </Button>
            )}
          </section>
        )}

        {cancelled !== null && (
          <section
            aria-label={t('cancelled')}
            className="flex flex-col rounded-2xl border bg-card p-4"
          >
            <b className="text-[15px] font-extrabold">{t('cancelled')}</b>
            <Row label={t('policyLine')} value={t(TIER_COPY[cancelled.refund.tier])} />
            <Row
              label={t('refundFeeLine')}
              value={moneyOf(cancelled.refund.feeCents, cancelled.currency, language)}
            />
            <Row
              label={t('refundLine')}
              value={moneyOf(cancelled.refund.refundCents, cancelled.currency, language)}
            />
          </section>
        )}
      </div>
      <Toasts toasts={toasts} undoLabel={t('undo')} />
    </div>
  );
}
